'use client';

/**
 * `FileDropZone` (LF-4642) — one shared drop pattern for the whole app.
 *
 * Presentational by design: it renders the drop surface (banner / compact /
 * mini, with an opt-in "AI extraction" sell) and the standard file list, and
 * fires callbacks. Upload transport (pre-signed URL, S3 PUT, progress,
 * rollback) stays with the caller — see `useFileDropZoneState` for an optional
 * lifecycle helper, or pass a controlled `files` array directly.
 */

import { useRef, type ChangeEvent } from 'react';
import { cn } from '@/lib/utils';
import { useDragAndDrop } from '@/hooks/use-drag-and-drop';
import { FileDropZoneSurface } from './file-drop-zone-surface';
import { FileDropZoneList } from './file-drop-zone-list';
import { resolveSell } from './sell';
import type { FileDropZoneProps } from './types';

export function FileDropZone({
  variant = 'compact',
  multiple = true,
  accept,
  disabled = false,
  readOnly = false,
  dropzone = true,
  sell = false,
  title,
  hintText,
  files = [],
  fileCount,
  onFilesAdded,
  onClick,
  secondaryAction,
  secondaryButtonAction,
  onRemove,
  onRetry,
  renderItem,
  className,
  'data-testid': dataTestId
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedSell = resolveSell(variant, sell);

  const emitFiles = (selected: File[]) => {
    if (selected.length > 0) onFilesAdded?.(selected);
  };

  // Drag path: accept everything dropped and let the caller / state hook
  // validate, so an unsupported drop surfaces as an error row rather than
  // silently vanishing. A non-droppable CTA (`dropzone={false}`) disables the
  // hook — its handlers still `preventDefault`, so a stray drop is swallowed
  // (not opened by the browser) rather than acted on.
  const { isDragging, getRootProps } = useDragAndDrop({
    onDrop: emitFiles,
    multiple,
    disabled: disabled || readOnly || !dropzone
  });

  const openFileDialog = () => inputRef.current?.click();

  // Droppable surfaces open the native picker; a non-droppable CTA hands off to
  // the caller (e.g. opens an upload dialog that owns the real drop surface).
  const activate = () => {
    if (dropzone) openFileDialog();
    else onClick?.();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected && selected.length > 0) {
      emitFiles(Array.from(selected));
      // Reset so re-selecting the same file fires change again.
      e.target.value = '';
    }
  };

  return (
    <div
      className={cn('flex flex-col gap-3', className)}
      data-testid={dataTestId}
    >
      {!readOnly && (
        <>
          <FileDropZoneSurface
            variant={variant}
            sell={resolvedSell}
            droppable={dropzone}
            isDragging={isDragging}
            disabled={disabled}
            title={title}
            hintText={hintText}
            fileCount={fileCount ?? files.length}
            onBrowse={activate}
            secondaryAction={secondaryAction}
            secondaryButtonAction={secondaryButtonAction}
            rootProps={getRootProps()}
          />
          {/* No native picker for a non-droppable CTA — it hands off via onClick. */}
          {dropzone && (
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              multiple={multiple}
              accept={accept}
              disabled={disabled}
              onChange={handleInputChange}
            />
          )}
        </>
      )}

      <FileDropZoneList
        files={files}
        onRemove={onRemove}
        onRetry={onRetry}
        readOnly={readOnly}
        renderItem={renderItem}
      />
    </div>
  );
}
